import React from 'react';
import checkCoins from '@/assets/imgs/check/check-coins.png';
import lockIcon from '@/assets/imgs/check/lock.webp';
import {toPriceStr} from '@components/utils';
import {Button, Image} from 'antd-mobile';
import {ReactComponent as CheckIcon} from '@/assets/imgs/check/check.svg';
import {cn} from '@utils/utils';
import {useSequentialModalContext} from '@basicComponents/sequential-modal/context';
import {goTo} from '@utils';
import {
  useCheckInData,
  RenderDayData,
} from '@/common-pages/hooks/useCheckInData';
import {CheckInStatus} from '@/common-pages/check-in/types';
import globalStore from '@services/global.state';

export const CHECK_IN_NAME = 'CHECK_IN_NAME';

const tips = [
  'Reach VIP 1 to enjoy bonus rewards',
  'The higher the VIP level, the richer the rewards',
];

// 逾期标识组件
const ExpiredRect = () => {
  return (
    <div className="bg-[#c6c6c6] px-0.5 py-[0.1875rem] rounded-full font-bold text-[#5F6975] text-base scale-50 flex items-center justify-center origin-top-left">
      Expired
    </div>
  );
};

function CheckInModal() {
  const {goToNext, onClose} = useSequentialModalContext();

  // 使用签到数据 hook
  const {renderList, isCheckingIn, isTodayChecked, checkIn, rawData} =
    useCheckInData();

  // 获取当前 VIP 等级
  const currentVipLevel = React.useMemo(() => {
    return rawData?.currentVipLevel || 0;
  }, [rawData]);

  // 根据 VIP 等级显示对应的提示文本
  const currentTip = React.useMemo(() => {
    return currentVipLevel > 0 ? tips[1] : tips[0];
  }, [currentVipLevel]);

  // 渲染单天的签到项
  const renderDay = (item: RenderDayData, i: number) => {
    const isLast = i === 6;

    // 使用 RenderDayData 中的 status 字段判断状态
    const isExpired = item.status === CheckInStatus.EXPIRED; // 过期
    const isLocked = item.status === CheckInStatus.LOCKED; // 未来日期（锁定）
    const isActive = item.status === CheckInStatus.AVAILABLE; // 当天可签到
    const isCompleted =
      item.status === CheckInStatus.COMPLETED ||
      item.status === CheckInStatus.CHECKED_IN; // 已签到

    return (
      <div className={cn('relative', isLast && 'col-span-2')}>
        <div
          className={cn(
            'text-t2 text-[0.625rem] font-black uppercase flex items-center justify-center border border-white/50 rounded-t-lg h-[1.5625rem]',
            isActive || isLocked
              ? 'bg-[linear-gradient(180deg,#FFCB67_0%,#FFAF13_100%)]'
              : 'bg-[linear-gradient(180deg,#d3d3d3_0%,#bababa_100%)]',
          )}>
          {item.day}
        </div>
        <div
          className={cn(
            'rounded-[0.25rem_0.25rem_0.5rem_0.5rem] border border-white/70 pt-8 w-full py-1 relative overflow-hidden -top-0.5',
            isActive || isLocked ? 'bg-[#FFE8BA]' : 'bg-[#E3E3E3]',
          )}>
          <Image
            className="absolute w-8 h-6 left-1/2 -translate-x-1/2 top-1"
            src={item.icon}
          />
          <div
            className={cn(
              'text-xs text-center font-black mt-8',
              isActive || isLocked ? 'text-[#F15802]' : 'text-[#5f6975]',
            )}>
            {toPriceStr(item.rewardAmount, {fixed: 0})}
          </div>
          <div className="absolute left-1.5 top-1.5">
            {isExpired && <ExpiredRect />}
            {isCompleted && <CheckIcon className="size-3.5" />}
          </div>
        </div>
        {isLocked && (
          <div className="absolute inset-0 bg-black/30 rounded-[0.25rem_0.25rem_0.5rem_0.5rem] flex items-end justify-center pb-[0.9375rem]">
            <Image className="w-6 h-8" src={lockIcon} />
          </div>
        )}
      </div>
    );
  };

  // 处理签到点击
  const handleCheckIn = async () => {
    // 如果当天已签到，跳转到签到页面
    if (isTodayChecked) {
      goTo('CheckIn');
      return;
    }

    try {
      const res = await checkIn();
      if (!res.autoRewardAmount) {
        goTo('CheckIn');
        onClose();
      }
    } catch (error) {
      globalStore.globalWaringTotal(
        'Check-in failed, the event hasn’t started yet.',
      );
    }
  };

  // 跳转到充值页面
  const toRecharge = () => {
    onClose();
    goTo('Recharge');
  };

  return (
    <div className="relative">
      <div className="relative w-80 pb-3 rounded-xl bg-[#FFECD2] border border-white/70 overflow-hidden">
        <div className="h-[5.5rem] pl-3 py-2.5 bg-[linear-gradient(277deg,#FFD67E_-10%,#FF61A5_30.42%,#E3452E_88.65%)] capitalize text-white relative overflow-hidden">
          <div className="text-[1.625rem] font-black leading-8">
            7-Day Check-in
          </div>
          <div className="text-xs font-bold text-white/80">
            Check in consecutively to get
          </div>
          <div className="text-xs font-bold text-white/80">wonderful gifts</div>
          <div
            className="absolute -right-[1.25rem] -top-[46%]  bg-full w-[10.625rem] h-[10.3125rem]"
            style={{
              backgroundImage: `url(${checkCoins})`,
            }}
          />
        </div>
        <div className="mt-3 px-3">
          <div className="text-t1 font-bold text-sm capitalize">
            Cumulative check-in
            <span className="text-[#F00] font-black text-base mx-0.5">
              {renderList.filter(item => item.isChecked).length}
            </span>
            days
          </div>
          <div className="grid grid-cols-4 gap-1 mt-3">
            {renderList.map(renderDay)}
          </div>
        </div>
        <div className="mx-3 mt-3">
          <Button
            className="text-white overflow-hidden text-sm font-bold w-full flex justify-center items-center border border-[#e26900] bg-gradient-to-b from-[#FFA800] to-[#FF6B00] shadow-[inset_0_2px_2px_0_rgba(255,255,255,0.5)] h-[2.3125rem] rounded-full"
            onClick={handleCheckIn}
            disabled={isTodayChecked}>
            {isCheckingIn
              ? 'Checking in...'
              : isTodayChecked
              ? 'Checked In'
              : 'Daily Check-in'}
          </Button>
        </div>
        <div className="mt-3 mx-3 flex items-center">
          <div className="text-[#733711] text-xs font-semibold flex-1">
            {currentTip}
          </div>
          <Button
            className="flex overflow-hidden justify-center items-center rounded-full h-6 w-[4.75rem] text-white text-[0.625rem] font-bold"
            onClick={toRecharge}
            style={{
              backgroundImage:
                'linear-gradient(270deg, #F35572 0%, #E3452E 100%)',
            }}>
            Recharge
          </Button>
        </div>
      </div>
      <div className="rounded-full border border-white flex justify-center items-center p-[0.6875rem] absolute -bottom-10 left-1/2 -translate-x-1/2">
        <svg
          className="size-3"
          viewBox="0 0 12 12"
          fill="none"
          onClick={goToNext}>
          <path
            d="M9.30566 1.28027C9.69618 0.889756 10.3292 0.889773 10.7197 1.28027C11.1103 1.6708 11.1102 2.30381 10.7197 2.69434L7.41406 6L10.7197 9.30566C11.1102 9.69618 11.1102 10.3292 10.7197 10.7197C10.3292 11.1102 9.69619 11.1103 9.30566 10.7197L6 7.41406L2.69434 10.7197C2.30381 11.1103 1.6708 11.1102 1.28027 10.7197C0.889793 10.3292 0.889765 9.69617 1.28027 9.30566L4.58594 6L1.28027 2.69434C0.889751 2.30381 0.88975 1.6708 1.28027 1.28027C1.6708 0.889751 2.30381 0.88975 2.69434 1.28027L6 4.58594L9.30566 1.28027Z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  );
}

export default CheckInModal;
